import type { Address } from "viem"
import type { Config } from "wagmi"
import { action, computed, makeObservable, observable, override, runInAction } from "mobx"
import { parseUnits } from "viem"

import type { EmitEvent, WeiValue } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { IApprovalModeProvider } from "@/domains/settings/interfaces"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import type { EventEmitter } from "@/domains/library"
import {
  quoteToken0FromToken1,
  quoteToken1FromToken0,
} from "@/domains/thick-v2/stores/deposit-counterpart-quote"
import { getSqrtRatioAtTick } from "@/domains/manual-cl-core"

import { THICK_V2_BASE_ADDRESSES } from "../constants"
import { increaseThickNfpInteraction } from "../interactions"
import type { ManualClPositionRepository } from "../positions/manual-cl-position-repository"
import type { ManualClPositionStore } from "../positions/manual-cl-position-store"
import { humanizeTransactionError } from "../utils/humanize-transaction-error"
import { ManagePositionStoreBase } from "./manage-position-store-base"

/**
 * Balance + allowance snapshot produced by `use-create-nfp-position-lens`.
 * The shape is duplicated here (rather than re-exported from the hook) because
 * the manage composites that call `submit()` compute it once per render and
 * pass it down as a plain DTO — this keeps the store free of hook imports.
 */
export type NfpPositionLensResult = {
  token0: {
    balance: WeiValue
    allowance: WeiValue
  }
  token1: {
    balance: WeiValue
    allowance: WeiValue
  }
}

/**
 * Manage-position store for the "increase liquidity" flow.
 *
 * Consumes the active position via `ManagePositionStoreBase.resolvedStatus`,
 * handles dependent-amount wiring against the pool's current sqrtPriceX96, and
 * on success publishes `ManualClLiquidityIncreasedEvent` so the position
 * orchestrator's GraphQL + on-chain queries re-execute.
 *
 * Approval state is deferred: the existing `increaseThickNfpInteraction`
 * handles approvals inline via `approveOwnerSpenderAllowance`. A future
 * migration will lift that logic into a centralised `AllowanceRepository`;
 * see `@todo MIGRATE` note below.
 *
 * @todo MIGRATE: approvalState0/1 to a shared AllowanceRepository once one
 * exists. For now the interaction handles approvals inline.
 */
export class IncreasePositionStore extends ManagePositionStoreBase {
  amount0: string = ""
  amount1: string = ""
  activeDepositInput: "token0" | "token1" | undefined = undefined

  isSubmitting: boolean = false
  submissionError: string | undefined = undefined

  constructor(
    positionRepository: ManualClPositionRepository,
    private readonly chainId: number,
    private readonly config: Config,
    private readonly eventBus: IEventBus,
    private readonly emitter: EventEmitter<EmitEvent>,
    private readonly walletStore: IWalletConnectionStore,
    private readonly approvalModeProvider: IApprovalModeProvider,
  ) {
    super(positionRepository)
    makeObservable(this, {
      amount0: observable,
      amount1: observable,
      activeDepositInput: observable,
      isSubmitting: observable,
      submissionError: observable,

      setAmount0FromUserInput: action,
      setAmount1FromUserInput: action,
      setActiveDepositInput: action,
      submit: action,
      reset: override,

      amount0Min: computed,
      amount1Min: computed,
      currentSqrtPriceX96: computed,
      usesNative: computed,
      canSubmit: computed,
    })
  }

  private get activePosition(): ManualClPositionStore | undefined {
    return this.position
  }

  /**
   * Prefers the pool's on-chain sqrtPriceX96 (freshest value from slot0); falls
   * back to the tick-derived sqrtRatio so dependent amounts still render
   * sensibly during the slot0 load window.
   */
  get currentSqrtPriceX96(): bigint | undefined {
    const pool = this.activePosition?.pool
    if (!pool) return undefined
    if (pool.currentSqrtPriceX96 !== undefined) return pool.currentSqrtPriceX96
    if (pool.currentTick === undefined) return undefined
    try {
      return getSqrtRatioAtTick(pool.currentTick)
    } catch {
      return undefined
    }
  }

  /**
   * True when either pool token is the chain's wrapped native (e.g. WETH on
   * Base). Drives "use native" toggles in the UI. Currently returns false; the
   * native-wrap flow is implemented inside the transaction encoder and does
   * not require branching at this store layer.
   *
   * @todo MIGRATE: derive from TokenStore.isWrappedNative once that flag lives
   * on the token model.
   */
  get usesNative(): boolean {
    return false
  }

  get canSubmit(): boolean {
    if (!this.activePosition) return false
    if (this.isSubmitting) return false
    if (!this.walletStore.isConnected) return false
    if (this.activeNfpmTokenId === undefined) return false
    const a0 = this.parseAmount(this.amount0, this.activePosition.pool.token0.decimals)
    const a1 = this.parseAmount(this.amount1, this.activePosition.pool.token1.decimals)
    return a0 > 0n || a1 > 0n
  }

  get amount0Min(): bigint {
    const position = this.activePosition
    if (!position) return 0n
    const desired = this.parseAmount(this.amount0, position.pool.token0.decimals)
    if (desired === 0n) return 0n
    const slippageMultiplier = BigInt(Math.floor((100 - this.slippage) * 100))
    return (desired * slippageMultiplier) / 10_000n
  }

  get amount1Min(): bigint {
    const position = this.activePosition
    if (!position) return 0n
    const desired = this.parseAmount(this.amount1, position.pool.token1.decimals)
    if (desired === 0n) return 0n
    const slippageMultiplier = BigInt(Math.floor((100 - this.slippage) * 100))
    return (desired * slippageMultiplier) / 10_000n
  }

  setActiveDepositInput(input: "token0" | "token1" | undefined): void {
    this.activeDepositInput = input
  }

  setAmount0FromUserInput(input: string): void {
    const position = this.activePosition
    if (!position) {
      this.amount0 = input
      return
    }
    this.amount0 = input
    this.activeDepositInput = "token0"
    const sqrtPriceX96Current = this.currentSqrtPriceX96
    if (sqrtPriceX96Current === undefined) return
    const amount0 = this.parseAmount(input, position.pool.token0.decimals)
    if (amount0 === 0n) {
      this.amount1 = ""
      return
    }
    const amount1 = quoteToken1FromToken0(amount0, {
      sqrtPriceX96Current,
      tickLower: position.tickLower,
      tickUpper: position.tickUpper,
      token0: position.pool.token0,
      token1: position.pool.token1,
      fee: position.pool.feeTier,
    })
    if (amount1 === undefined) {
      this.amount1 = ""
      return
    }
    this.amount1 = position.pool.token1.formatUnits(amount1)
  }

  setAmount1FromUserInput(input: string): void {
    const position = this.activePosition
    if (!position) {
      this.amount1 = input
      return
    }
    this.amount1 = input
    this.activeDepositInput = "token1"
    const sqrtPriceX96Current = this.currentSqrtPriceX96
    if (sqrtPriceX96Current === undefined) return
    const amount1 = this.parseAmount(input, position.pool.token1.decimals)
    if (amount1 === 0n) {
      this.amount0 = ""
      return
    }
    const amount0 = quoteToken0FromToken1(amount1, {
      sqrtPriceX96Current,
      tickLower: position.tickLower,
      tickUpper: position.tickUpper,
      token0: position.pool.token0,
      token1: position.pool.token1,
      fee: position.pool.feeTier,
    })
    if (amount0 === undefined) {
      this.amount0 = ""
      return
    }
    this.amount0 = position.pool.token0.formatUnits(amount0)
  }

  async submit(params: { lens: NfpPositionLensResult }): Promise<void> {
    const position = this.activePosition
    if (!position) return
    const nfpmTokenId = this.activeNfpmTokenId
    if (nfpmTokenId === undefined) {
      runInAction(() => {
        this.submissionError = "NFP ID is still resolving. Please try again in a moment."
      })
      return
    }
    const account = this.walletStore.connectedAddress?.raw
    if (!account) return

    runInAction(() => {
      this.isSubmitting = true
      this.submissionError = undefined
    })

    try {
      const amount0Desired = this.parseAmount(this.amount0, position.pool.token0.decimals)
      const amount1Desired = this.parseAmount(this.amount1, position.pool.token1.decimals)
      const deadline = this.getSubmitDeadline()

      const result = await increaseThickNfpInteraction({
        account,
        chainId: this.chainId,
        config: this.config,
        tokenId: nfpmTokenId,
        poolAddress: position.pool.address.raw as Address,
        token0: {
          address: position.pool.token0.address.raw as Address,
          decimals: position.pool.token0.decimals,
          symbol: position.pool.token0.symbol,
        },
        token1: {
          address: position.pool.token1.address.raw as Address,
          decimals: position.pool.token1.decimals,
          symbol: position.pool.token1.symbol,
        },
        amount0Desired,
        amount1Desired,
        amount0Min: this.amount0Min,
        amount1Min: this.amount1Min,
        deadline,
        nfpManagerAddress: THICK_V2_BASE_ADDRESSES.nfPositionManager,
        emitter: this.emitter,
        lens: params.lens,
        approvalMode: this.approvalModeProvider.getApprovalMode(),
        eventBus: this.eventBus,
      })

      if (result && result.success === false) {
        const humanized = humanizeTransactionError(result.error)
        if (humanized !== null) {
          runInAction(() => {
            this.submissionError = humanized
          })
        }
      }
    } catch (error) {
      const humanized = humanizeTransactionError(error)
      if (humanized !== null) {
        runInAction(() => {
          this.submissionError = humanized
        })
      }
    } finally {
      runInAction(() => {
        this.isSubmitting = false
      })
    }
  }

  reset(): void {
    super.reset()
    this.amount0 = ""
    this.amount1 = ""
    this.activeDepositInput = undefined
    this.isSubmitting = false
    this.submissionError = undefined
  }

  private parseAmount(value: string, decimals: number): bigint {
    if (!value) return 0n
    try {
      return parseUnits(value, decimals)
    } catch {
      return 0n
    }
  }
}
